import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container, Spinner } from "reactstrap";
import styled from "styled-components";
import { CostLine, CreditsPaywallSheet } from "../../components/Credits";
import NewFooter from "../../components/Footers/NewFooter";
import ListingImportProgress from "../../components/ListingImportProgress/ListingImportProgress";
import PhotoImportCoverage from "../../components/ListingImportProgress/PhotoImportCoverage";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL } from "../../constants";
import {
  ACTION_COSTS,
  ADDABLE_ARTIFACTS,
  ARTIFACT_LABELS,
  ARTIFACT_NOTES,
  CREDITS_PRIMER_KEY,
  formatCredits,
  formatCreditsNumber,
  insufficientCreditsFrom,
  takeCreditsResume,
} from "../../constants/credits";
import { useAccount } from "../../contexts/AccountContext";
import useIntakeCooldown from "../../hooks/useIntakeCooldown";
import useIntakeUpload from "../../hooks/useIntakeUpload";
import usePaidAttempt from "../../hooks/usePaidAttempt";
import { CONFIRM_LISTING_SUCCESS } from "../../redux/actionTypes";
import { cancelListing, fetchListing, uploadImages } from "../../redux/actions/assessment";
import { track } from "../../utils/analytics";
import {
  authHeader,
  clearFunnelToken,
  getFunnelToken,
  getToken,
  listingAuthHeader,
} from "../../utils/auth";
import { confirmationCreditCopy } from "../../utils/confirmationCreditCopy";
import {
  clearConfirmationDraft,
  confirmedGapFillsMatch,
  loadConfirmationDraft,
  pendingGapFills,
  saveConfirmationDraft,
  validateConfirmationChoices,
} from "../../utils/confirmationDrafts";
import { flattenError } from "../../utils/errorDisplay";
import { intakeErrorText } from "../../utils/intakeLimits";
import { loadPaidAttempt, paidAttemptCurrentJob } from "../../utils/paidAttempts";
import { storageGet, storageSet } from "../../utils/storage";

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 80px 0 60px;
`;

const CheckoutCard = styled.div`
  background: #fff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 18px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  max-width: 660px;
  margin: 0 auto;
  padding: 26px 28px;

  @media (max-width: 767.98px) {
    padding: 20px 18px;
    border-radius: 14px;
  }
`;

const PropertyRow = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;

  @media (max-width: 575.98px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Thumb = styled.img`
  width: 132px;
  height: 99px;
  object-fit: cover;
  border-radius: 10px;
  flex: 0 0 auto;
  display: block;

  @media (max-width: 575.98px) {
    width: 100%;
    height: 160px;
  }
`;

const ThumbFallback = styled.div`
  width: 132px;
  height: 99px;
  border-radius: 10px;
  flex: 0 0 auto;
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary-700, #085552);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  text-align: center;
  padding: 6px;

  @media (max-width: 575.98px) {
    width: 100%;
    height: 120px;
  }
`;

const PropertyMeta = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--brand-primary, #0e7e7a);
  margin: 0 0 6px;
`;

const PropertyAddress = styled.h1`
  font-size: 21px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--brand-ink, #0b1c1e);
  margin: 0 0 4px;

  @media (max-width: 767.98px) {
    font-size: 18px;
  }
`;

const PropertyFacts = styled.p`
  font-size: 14px;
  color: var(--brand-ink-3, #5c7074);
  margin: 0;
`;

const NotYourProperty = styled.p`
  font-size: 12px;
  color: var(--brand-ink-4, #8a9a9d);
  margin: 8px 0 0;

  button {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: var(--brand-ink-3, #5c7074);
    cursor: pointer;
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid var(--brand-border, #e3ebec);
  margin: 20px 0 18px;
`;

const DeliveryPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary-700, #085552);
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 999px;
  padding: 8px 15px;
  margin-bottom: 18px;
`;

const PayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  background: var(--brand-bg-soft, #f7fafb);
  border-radius: 12px;
  padding: 16px 18px;

  @media (max-width: 575.98px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const OrderButton = styled.button`
  background: var(--brand-primary);
  color: #fff;
  border: none;
  border-radius: var(--brand-radius-lg);
  padding: 16px 48px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--brand-shadow-primary);
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: var(--brand-primary-600);
    transform: translateY(-1px);
    box-shadow: var(--brand-shadow-primary-hover);
  }

  &:disabled {
    background: var(--brand-border-strong);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 14px 32px;
  }
`;

const FootRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const Guarantee = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--brand-ink-2, #2b3f42);
  margin-top: 6px;  

  svg {
    color: var(--brand-primary, #0e7e7a);
    flex-shrink: 0;
  }

  strong {
    font-weight: 700;
    color: var(--brand-ink, #0b1c1e);
  }
`;

const MissingDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 10px;

  label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 4px;
  }

  input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.92rem;
  }
`;

const MissingPhotos = styled.div`
  margin: 14px 0 0;
  padding: 14px 16px;
  border: 1px solid #fcd34d;
  background: #fffbeb;
  border-radius: 10px;
  font-size: 0.92rem;
  color: #78350f;
  line-height: 1.5;
`;

const MissingPhotosActions = styled.div`
  margin-top: 10px;

  label {
    display: inline-block;
    padding: 8px 18px;
    border-radius: 50px;
    background: #0e7e7a;
    color: #ffffff;
    font-weight: 600;
    cursor: pointer;
  }

  input[type="file"] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
  }

  /* The quiet second choice beside "Add photos" — a link, not a rival button. */
  button {
    margin-left: 14px;
    padding: 0;
    border: 0;
    background: none;
    color: #78350f;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }
`;

// One calm line once photos are skipped: a fact, and where photos go later.
const PhotosSkippedNote = styled.p`
  margin: 14px 0 0;
  font-size: 0.92rem;
  color: #6b7280;
  line-height: 1.5;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.85rem;
  color: #4a4a4a;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #6b7280;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.85rem;
  margin-top: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;

  p {
    font-size: 1.1rem;
    color: #6b7280;
  }
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  padding: 40px 20px;

  h2 {
    font-size: 1.75rem;
    color: #dc2626;
    margin-bottom: 16px;
  }

  p {
    font-size: 1.1rem;
    color: #6b7280;
    margin-bottom: 24px;
  }

  button {
    background-color: #6b7280;
    color: white;
    border: none;
    border-radius: 50px;
    padding: 12px 32px;
    font-size: 1rem;
    cursor: pointer;

    &:hover {
      background-color: #4b5563;
    }
  }
`;

const TemplatePickerRow = styled.div`
  max-width: 360px;
  margin: 0 auto 20px;
  text-align: left;
`;

// --- The Prepare sheet (credits, CREDITS_FLOW_DESIGN v3 §4) ---
// A quiet order sheet: the analysis line, four plain add-file checkboxes (all
// off), one fact line, one button. Prices are menu information, shown once.
const Primer = styled.div`
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary-700, #085552);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13.5px;
  line-height: 1.5;

  p {
    margin: 0;
  }
`;

const MenuLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 16px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);

  span {
    font-weight: 500;
    color: var(--brand-ink-3, #5c7074);
    white-space: nowrap;
  }
`;

const MenuSub = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: var(--brand-ink-3, #5c7074);
  margin: 4px 0 0;
`;

const AddFilesLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-ink, #0b1c1e);
  margin: 18px 0 6px;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 14px;
  color: var(--brand-ink-2, #2b3f42);
  margin: 0 0 6px;
  cursor: pointer;

  input {
    flex: 0 0 auto;
    position: relative;
    top: 1px;
  }

  em {
    font-style: normal;
    color: var(--brand-ink-4, #8a9a9d);
  }
`;

const PrepareRow = styled(PayRow)`
  margin-top: 18px;
`;

const ANALYSIS_CONTENTS =
  "Listing Strategy, Quick CMA, Listing Description, photo review, and property facts — shareable on the web; add a PDF anytime.";

/** @param {number | string | null | undefined} n */
function formatCount(n) {
  const num = Number(n);
  if (n === null || n === undefined || n === "" || !Number.isFinite(num)) return null;
  return String(num);
}

/** @param {Listing | null} listing */
function formatFacts(listing) {
  if (!listing) return "";
  const parts = [];
  const beds = formatCount(listing.bedrooms);
  const baths = formatCount(listing.bathrooms);
  if (beds) parts.push(`${beds} bed`);
  if (baths) parts.push(`${baths} bath`);
  if (listing.living_area) parts.push(`${Number(listing.living_area).toLocaleString()} sqft`);
  return parts.join(" \u00b7 ");
}

/**
 * @typedef {Object} Listing
 * @property {string} uuid
 * @property {string} [street_address]
 * @property {string} [city]
 * @property {string} [address_state]
 * @property {string} [zipcode]
 * @property {string} [cover_image_url]
 * @property {Array<{id: number, file: string, uploaded_at: string}>} [images]
 * @property {number} [bedrooms]
 * @property {number} [bathrooms]
 * @property {number} [living_area]
 * @property {number | string | null} [price]
 * @property {string} [payment_time]
 * @property {string} [source]
 */

/** @param {Listing | null} listing */
function formatAddress(listing) {
  if (!listing) return "";
  const parts = [];
  if (listing.street_address) parts.push(listing.street_address);
  if (listing.city) parts.push(listing.city);
  const region = [listing.address_state, listing.zipcode].filter(Boolean).join(" ");
  if (region) parts.push(region);
  return parts.join(", ");
}

function NewListingConfirmation() {
  // Confirm idempotency (WIP hardening): once a confirm request has LEFT the
  // browser its outcome is unknowable until reconciled, so cancellation must
  // become unavailable before the request is sent.
  const [confirmedForCheckout, setConfirmedForCheckout] = useState(false);
  const [confirmationUnsafeToCancel, setConfirmationUnsafeToCancel] = useState(false);
  const confirmationAttemptedRef = useRef(false);
  const cancellationInFlightRef = useRef(false);
  const [checkoutError, setCheckoutError] = useState(/** @type {string | null} */ (null));
  const [detailFixes, setDetailFixes] = useState(/** @type {Record<string, string>} */ ({}));
  const choicesRef = useRef(
    /** @type {({uuid: string} & import("../../utils/confirmationDrafts").ConfirmationChoices) | null} */ (
      null
    )
  );
  const draftRef = useRef(
    /** @type {import("../../utils/confirmationDrafts").ConfirmationDraft | null} */ (null)
  );
  const [choicesReadyUuid, setChoicesReadyUuid] = useState(/** @type {string | null} */ (null));
  const [choiceWarning, setChoiceWarning] = useState("");
  const resumedDraftRef = useRef("");
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );
  const [imageFailed, setImageFailed] = useState(false);
  // Trello #52: a listing may be prepared without photos. The server's flag
  // arrives on the listing payload; the local one covers this visit.
  const [photosSkippedHere, setPhotosSkippedHere] = useState(false);
  const [skippingPhotos, setSkippingPhotos] = useState(false);
  const [skipPhotosError, setSkipPhotosError] = useState(/** @type {string | null} */ (null));
  // A signed-in agent's saved prep templates (e.g. Relist / New listing /
  // Rental): shown as a picker when they have more than one, and the chosen
  // name rides the prepare so THIS prep is built from that template.
  const [prepTemplates, setPrepTemplates] = useState(/** @type {any[]} */ ([]));
  const [selectedTemplate, setSelectedTemplate] = useState("");
  // The shared account read (templates and the balance). One fetch for the
  // app; refreshed after a debit.
  const { account, credits, loading: accountLoading, error: accountError, refresh } = useAccount();
  // The Prepare sheet's state. Add-ons are the artifacts checked for this run
  // (all off by default — D18); the primer is the one-time first-spend
  // explainer; the paywall is the server's 409, remembering what was checked
  // so a top-up can come back to the same choice.
  const [addons, setAddons] = useState(/** @type {string[]} */ ([]));
  const [primerSeen, setPrimerSeen] = useState(
    () => storageGet(CREDITS_PRIMER_KEY, "local") === "1"
  );
  const [paywall, setPaywall] = useState(
    /** @type {{ block: { balance: number, cost: number, action: string }, resume: { kind: "prepare", artifacts: string[] } } | null} */ (
      null
    )
  );
  // After a pack bought from the paywall: the same files re-checked and one
  // fact line, "Credits added — you have N", shown once.
  const [creditsAdded, setCreditsAdded] = useState(
    /** @type {{ balance: number | null } | null} */ (null)
  );
  const [isPreparing, setIsPreparing] = useState(false);
  const prepareInFlightRef = useRef(false);
  const prepareButtonRef = useRef(/** @type {HTMLButtonElement | null} */ (null));

  useEffect(() => {
    if (!account) return;
    const templates = account.report_templates || [];
    setPrepTemplates(templates);
    // A refreshed balance must never reset a chosen or restored template.
  }, [account]);

  const dispatch = useDispatch();
  const history = useHistory();
  const listing = useSelector((/** @type {any} */ state) => state.listings.currentListing);
  const activeCheckoutUuidRef = useRef(listing?.uuid);
  activeCheckoutUuidRef.current = listing?.uuid;
  const globalLoading = useSelector(
    (/** @type {any} */ state) => state.listings.confirmationLoading
  );
  const activeListingId = useSelector((/** @type {any} */ state) => state.listings.activeListingId);
  const imageUploadLoading = useSelector(
    (/** @type {any} */ state) => state.listings.imageUploadLoading
  );
  const imageUploadError = useSelector(
    (/** @type {any} */ state) => state.listings.imageUploadError
  );
  const intakeCooldown = useIntakeCooldown();
  const missingPhotoUpload = useIntakeUpload({
    targetKey: listing?.uuid || "",
    upload: (files) => /** @type {any} */ (dispatch)(uploadImages(listing.uuid, files)),
  });

  // Load the listing on mount if we have an id in Redux (hydrated from
  // sessionStorage by store.js). If there's no id, render falls through
  // to the "Listing Not Found" branch — no silent redirect.
  //
  // We want this to run once on mount with the id we booted with; we
  // don't want to re-fetch if activeListingId later flips to null
  // (that's the fetch-failure path, handled by the reducer). A ref
  // captures the mount-time id so the effect's deps can be accurate.
  const bootIdRef = useRef(activeListingId);
  const [verifiedBootUuid, setVerifiedBootUuid] = useState(/** @type {string | null} */ (null));
  const [bootReadRevision, setBootReadRevision] = useState(0);
  const [bootReadError, setBootReadError] = useState(false);
  useEffect(() => {
    const bootId = bootIdRef.current;
    let live = true;
    if (bootId) {
      setBootReadError(false);
      if (bootReadRevision > 0) setVerifiedBootUuid(null);
      void (
        /** @type {any} */ (dispatch)(fetchListing(bootId)).then((/** @type {any} */ result) => {
          if (!live) return;
          if (result?.ok && result.data?.uuid === bootId) setVerifiedBootUuid(bootId);
          else setBootReadError(true);
        })
      );
    }
    return () => {
      live = false;
    };
  }, [dispatch, bootReadRevision]);
  // Cached Redux facts cannot authorize a restored purchase before the first
  // authenticated GET has verified this exact listing's current import state.
  const listingIsFresh = !bootIdRef.current || verifiedBootUuid === listing?.uuid;

  const choicesReady = choicesReadyUuid === listing?.uuid;
  useEffect(() => {
    if (!listingIsFresh || !listing?.uuid || choicesRef.current?.uuid === listing.uuid) return;
    const saved = loadConfirmationDraft(listing.uuid);
    const choices = saved || {
      detailFixes: {},
      template: "",
      artifacts: [],
      resumeRequested: false,
    };
    choicesRef.current = { ...choices, uuid: listing.uuid };
    draftRef.current = saved;
    setDetailFixes(choices.detailFixes);
    setSelectedTemplate(choices.template);
    setAddons(choices.artifacts);
    setChoiceWarning(
      saved && !saved.persisted
        ? "These choices are only kept in this tab. Refreshing or leaving for payment could lose them."
        : ""
    );
    setChoicesReadyUuid(listing.uuid);
  }, [listing?.uuid, listingIsFresh]);

  const updateChoices = useCallback(
    (
      /** @type {Partial<import("../../utils/confirmationDrafts").ConfirmationChoices>} */ updates
    ) => {
      const previous = choicesRef.current;
      if (!listing?.uuid || !previous || previous.uuid !== listing.uuid) return null;
      const choices = { ...previous, ...updates };
      choicesRef.current = choices;
      setDetailFixes(choices.detailFixes);
      setSelectedTemplate(choices.template);
      setAddons(choices.artifacts);
      const saved = saveConfirmationDraft(listing.uuid, choices);
      if (saved) draftRef.current = saved;
      setChoiceWarning(
        !saved
          ? "These choices could not be saved. Keep this page open and check your entries."
          : !saved.persisted
            ? "These choices are only kept in this tab. Refreshing or leaving for payment could lose them."
            : !validateConfirmationChoices(choices)
              ? "Some property details are incomplete or invalid. Your entries are saved; check them before preparing."
              : ""
      );
      return saved;
    },
    [listing?.uuid]
  );

  const confirmationError = useSelector(
    (/** @type {any} */ state) => state.listings.confirmationError
  );

  const listingConfirmed = !!listing?.user_confirmed || confirmedForCheckout;
  const importNeedsReview =
    !!listing?.import && !["ready", "cancelled"].includes(listing.import.status);

  const handleGoBack = async () => {
    if (cancellationInFlightRef.current) return;
    const cancelledDraftId = draftRef.current?.id;
    // A confirmed listing is awaiting payment and CANNOT be cancelled (the
    // backend rejects it). Cancelling + clearing the id anyway would orphan a
    // confirmed-but-unpaid listing the customer can no longer get back to. So
    // only cancel + clear when the listing is actually cancellable; for a
    // confirmed one, keep the reference so they can return and pay.
    if (listing?.uuid && !listingConfirmed && !confirmationAttemptedRef.current) {
      cancellationInFlightRef.current = true;
      let result;
      try {
        result = await /** @type {any} */ (dispatch)(cancelListing(listing.uuid));
      } finally {
        cancellationInFlightRef.current = false;
      }
      // CANCEL_LISTING_SUCCESS clears the active id. If cancellation fails,
      // retain it so the user can resume instead of orphaning live server state.
      if (!result?.ok) return;
      clearConfirmationDraft(listing.uuid, cancelledDraftId);
    }
    history.push(listing?.source === "manual" ? "/submit" : "/verify");
  };

  // Claim the anonymous listing with the funnel token, then confirm it —
  // the ownership handoff that must complete before the debit.
  /** @returns {Promise<boolean>} whether the prepare may safely continue */
  const ensureClaimedAndConfirmed = useCallback(
    async (/** @type {Record<string,string>} */ requestedFixes) => {
      if (!listing?.uuid || !listingIsFresh || !choicesReady || importNeedsReview) return false;
      const isCurrent = () =>
        mountedRef.current &&
        activeCheckoutUuidRef.current === listing.uuid &&
        choicesRef.current?.uuid === listing.uuid;
      if (!isCurrent()) return false;
      const gapFills = pendingGapFills(listing, requestedFixes);
      const funnelToken = getFunnelToken(listing.uuid);
      if (funnelToken && getToken()) {
        // Claim the anonymous listing to this account before the debit. (A
        // guest never reaches here — Prepare is account-first.)
        await axios.post(
          `${API_URL}/listings/${listing.uuid}/claim/`,
          {},
          { headers: { ...authHeader(), "X-Listino-Funnel-Token": funnelToken }, timeout: 15000 }
        );
        clearFunnelToken(listing.uuid);
        if (!isCurrent()) return false;
      }
      if (!listingConfirmed || Object.keys(gapFills).length > 0) {
        // Confirmation can commit even when its response is lost. Make the
        // cancellation path unavailable before the request leaves the browser;
        // a retry or authoritative fetch can safely reconcile either outcome.
        confirmationAttemptedRef.current = true;
        setConfirmationUnsafeToCancel(true);
        let response;
        try {
          // Send only non-blank typed values; the API fills gaps and never
          // overwrites imported facts (Trello #38).
          response = await axios.post(`${API_URL}/listings/${listing.uuid}/confirm/`, gapFills, {
            headers: listingAuthHeader(listing.uuid),
            timeout: 15000,
          });
          if (!isCurrent()) return false;
          if (
            response.data?.uuid !== listing.uuid ||
            response.data?.user_confirmed !== true ||
            !confirmedGapFillsMatch(response.data, gapFills)
          ) {
            throw new Error(
              "We could not verify the corrected details. Please retry before preparing."
            );
          }
        } catch (error) {
          if (!isCurrent()) return false;
          const responseStatus = /** @type {{response?: {status?: number}}} */ (error).response
            ?.status;
          let reconciled = false;
          try {
            const authoritative = await axios.get(`${API_URL}/listings/${listing.uuid}/`, {
              headers: listingAuthHeader(listing.uuid),
              timeout: 15000,
            });
            if (!isCurrent()) return false;
            reconciled = authoritative.data?.uuid === listing.uuid;
            if (
              reconciled &&
              authoritative.data?.user_confirmed === true &&
              confirmedGapFillsMatch(authoritative.data, gapFills)
            ) {
              setConfirmedForCheckout(true);
              dispatch({
                type: CONFIRM_LISTING_SUCCESS,
                payload: { ...listing, ...authoritative.data, user_confirmed: true },
              });
              return true;
            }
            if (reconciled && !authoritative.data?.user_confirmed) {
              confirmationAttemptedRef.current = false;
              setConfirmedForCheckout(false);
              setConfirmationUnsafeToCancel(false);
            }
          } catch (reconciliationError) {
            if (!isCurrent()) return false;
            const reconciliationStatus = /** @type {{response?: {status?: number}}} */ (
              reconciliationError
            ).response?.status;
            if ([401, 403, 404].includes(reconciliationStatus || 0)) {
              confirmationAttemptedRef.current = false;
              setConfirmedForCheckout(false);
              setConfirmationUnsafeToCancel(false);
            }
            // If even the authoritative read is unavailable, preserve the active
            // listing for a safe retry instead of guessing that confirmation failed.
          }
          if (!reconciled && [400, 401, 403, 404].includes(responseStatus || 0)) {
            confirmationAttemptedRef.current = false;
            setConfirmedForCheckout(false);
            setConfirmationUnsafeToCancel(false);
          }
          throw error;
        }
        const confirmedListing = {
          ...listing,
          ...(response.data || {}),
          user_confirmed: true,
        };
        setConfirmedForCheckout(true);
        dispatch({ type: CONFIRM_LISTING_SUCCESS, payload: confirmedListing });
      }
      return true;
    },
    [listing, listingConfirmed, listingIsFresh, choicesReady, importNeedsReview, dispatch]
  );

  // The cost of this Prepare as configured (analysis + checked files).
  const prepareCost =
    ACTION_COSTS.analysis + addons.reduce((sum, key) => sum + (ACTION_COSTS[key] || 0), 0);

  /** @param {string} key */
  const toggleAddon = (key) => {
    const current = choicesRef.current?.artifacts || [];
    updateChoices({
      artifacts: current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    });
  };

  const paidPrepareRef = useRef(/** @type {ReturnType<typeof usePaidAttempt>|null} */ (null));
  const purchaseChoicesRef = useRef(/** @type {{uuid:string,id:string}|null} */ (null));
  const trackedPurchaseRef = useRef("");
  const paidPrepare = usePaidAttempt({
    scope: { action: "prepare", targetUuid: listing?.uuid || "" },
    enabled:
      !!getToken() && listingIsFresh && choicesReady && !importNeedsReview && !!listing?.uuid,
    onAccepted: (receipt, record) => {
      const job = paidAttemptCurrentJob(receipt);
      const exactUuid = job?.computation_uuid || receipt.attempt.computation_uuid;
      if (!receipt.replacement_chain_complete || !job || !exactUuid) {
        setCheckoutError(
          "This preparation request was accepted, but its current result is not available yet. Check status again."
        );
        return;
      }
      if (job.status === "failed") {
        const refunded = receipt.settlement.refunded_credits;
        setCheckoutError(
          `This preparation attempt did not finish.${refunded > 0 ? ` ${formatCredits(refunded)} returned for this request.` : ""}`
        );
        paidPrepareRef.current?.acknowledge(record.attempt_id);
        refresh();
        return;
      }
      const captured = purchaseChoicesRef.current;
      if (captured?.uuid === record.target_uuid)
        clearConfirmationDraft(record.target_uuid, captured.id);
      storageSet(CREDITS_PRIMER_KEY, "1", "local");
      setPrimerSeen(true);
      if (trackedPurchaseRef.current !== record.attempt_id) {
        trackedPurchaseRef.current = record.attempt_id;
        track("credits_spent", {
          action: "analysis",
          cost: receipt.settlement.charged_credits,
          listing_uuid: record.target_uuid,
          attempt_id: record.attempt_id,
        });
      }
      refresh();
      // Accepted work remains recoverable until its exact terminal job is
      // adopted. The success screen itself never guesses from a latest job.
      if (job.status === "completed") paidPrepareRef.current?.acknowledge(record.attempt_id);
      history.push(`/success?listing_uuid=${encodeURIComponent(record.target_uuid)}`);
    },
    onRejected: (receipt, record) => {
      const response = receipt.attempt.response;
      if (!paidPrepareRef.current?.acknowledge(record.attempt_id)) return;
      const rejected = { response: { status: response.status_code, data: response.data } };
      const block = insufficientCreditsFrom(rejected);
      if (
        block &&
        [block.balance, block.cost].every((value) => Number.isSafeInteger(value) && value >= 0)
      ) {
        const saved = draftRef.current;
        if (!saved?.persisted) {
          setCheckoutError(
            "Your choices are only kept in this tab. Enable browser storage before leaving to buy credits."
          );
          return;
        }
        setCreditsAdded(null);
        setPaywall({ block, resume: { kind: "prepare", artifacts: record.request.artifacts } });
        refresh();
        return;
      }
      setCheckoutError(
        typeof response.data?.error === "string"
          ? response.data.error
          : "This preparation request was not accepted. Review your choices before trying again."
      );
    },
  });
  paidPrepareRef.current = paidPrepare;
  const { check: checkPreparation, submit: submitPreparation } = paidPrepare;

  // Prepare this listing — claim + confirm, then ONE debit call. Guests
  // create a free account first (account-first, D12); the resume stash
  // carries what they had checked through the sign-in detour so the prepare
  // re-fires with the same files.
  const handlePrepare = useCallback(
    /** @param {{ artifacts?: string[]; template?: string; detailFixes?: Record<string,string>; automatic?: boolean; nativeEvent?: unknown }} [overrides] */
    async (overrides = {}) => {
      const opts =
        overrides && typeof overrides === "object" && !("nativeEvent" in overrides)
          ? overrides
          : {};
      // Recovery takes precedence over editable choices and non-paid claim /
      // confirm work. A second click on an uncertain request is GET-only.
      if (getToken() && listingIsFresh && choicesReady && listing?.uuid) {
        try {
          if (loadPaidAttempt({ action: "prepare", targetUuid: listing.uuid })) {
            await checkPreparation();
            return;
          }
        } catch (failure) {
          setCheckoutError(
            failure instanceof Error ? failure.message : "Check browser storage before preparing."
          );
          return;
        }
      }
      const chosen = validateConfirmationChoices({
        detailFixes: opts.detailFixes ?? choicesRef.current?.detailFixes ?? {},
        template: opts.template ?? choicesRef.current?.template ?? "",
        artifacts: opts.artifacts ?? choicesRef.current?.artifacts ?? [],
        resumeRequested: false,
      });
      if (!chosen) {
        setCheckoutError("Check the missing property details before preparing.");
        return;
      }
      const { artifacts, template, detailFixes: requestedFixes } = chosen;
      if (prepareInFlightRef.current) return;
      if (!listingIsFresh || !choicesReady || importNeedsReview) {
        setCheckoutError("Wait for the listing import or stop it before preparing.");
        return;
      }
      if (!listing || !listing.uuid) {
        setCheckoutError("We couldn't find this listing. Go back and re-enter the address.");
        return;
      }
      const savedAttempt = updateChoices(chosen);
      if (opts.automatic && !savedAttempt?.persisted) {
        setCheckoutError(
          "We could not safely save the resumed choices. Check browser storage and prepare manually; nothing was started automatically."
        );
        return;
      }
      const toSignIn = () => {
        const saved = updateChoices({ ...chosen, resumeRequested: true });
        if (!saved?.persisted) {
          setCheckoutError(
            "Your browser could not save these choices for sign-in. Enable browser storage and try again; your entries are still in this tab."
          );
          return;
        }
        history.push(`/login?next=${encodeURIComponent("/listing-confirmation/")}`);
      };
      if (!getToken()) {
        toSignIn();
        return;
      }
      // The API validates exact template names, including safe funded replays.
      prepareInFlightRef.current = true;
      setIsPreparing(true);
      setCheckoutError(null);
      try {
        const confirmed = await ensureClaimedAndConfirmed(requestedFixes);
        if (
          !mountedRef.current ||
          activeCheckoutUuidRef.current !== listing.uuid ||
          choicesRef.current?.uuid !== listing.uuid
        )
          return;
        if (!confirmed) {
          setIsPreparing(false);
          prepareInFlightRef.current = false;
          return;
        }
        if (savedAttempt?.id)
          purchaseChoicesRef.current = { uuid: listing.uuid, id: savedAttempt.id };
        await submitPreparation({ artifacts, prep_template: template });
      } catch (error) {
        console.error("Prepare error:", error);
        if (
          !mountedRef.current ||
          activeCheckoutUuidRef.current !== listing.uuid ||
          choicesRef.current?.uuid !== listing.uuid
        )
          return;
        setIsPreparing(false);
        prepareInFlightRef.current = false;
        const block = insufficientCreditsFrom(error);
        if (block) {
          // The server is the authority on the balance: open the sheet in
          // place with ITS numbers; the listing underneath stays as it is,
          // and the checked files ride the return stash.
          if (!savedAttempt?.persisted) {
            setCheckoutError(
              "Your choices are only kept in this tab. Enable browser storage and retry before leaving to buy credits."
            );
            return;
          }
          setCreditsAdded(null);
          setPaywall({ block, resume: { kind: "prepare", artifacts } });
          refresh();
          return;
        }
        const axiosError =
          /** @type {{ response?: { status?: number, data?: { error?: string } } }} */ (error);
        if (axiosError.response?.status === 401) {
          toSignIn();
          return;
        }
        setCheckoutError(
          `${
            axiosError.response?.data?.error ||
            "We couldn't start preparing this listing. Please try again in a moment."
          } Check this listing before retrying; your choices are ${savedAttempt?.persisted ? "saved" : "still in this tab"}.`
        );
      } finally {
        if (mountedRef.current && activeCheckoutUuidRef.current === listing.uuid) {
          setIsPreparing(false);
          prepareInFlightRef.current = false;
        }
      }
    },
    [
      listing,
      listingIsFresh,
      choicesReady,
      importNeedsReview,
      updateChoices,
      checkPreparation,
      submitPreparation,
      history,
      ensureClaimedAndConfirmed,
      refresh,
    ]
  );

  // Restore every choice first, then pass that snapshot directly. An attempted
  // resume stays saved but cannot re-fire on account refresh.
  useEffect(() => {
    if (!listingIsFresh || !choicesReady || importNeedsReview || !getToken() || !listing?.uuid)
      return;
    const saved = draftRef.current;
    if (!saved?.resumeRequested || resumedDraftRef.current === saved.id) return;
    resumedDraftRef.current = saved.id;
    void handlePrepare({
      artifacts: saved.artifacts,
      template: saved.template,
      detailFixes: saved.detailFixes,
      automatic: true,
    });
  }, [listing?.uuid, listingIsFresh, choicesReady, importNeedsReview, handlePrepare]);

  // Back from a pack bought at the paywall: re-check the same files and state
  // the fact once. The tap itself is the agent's — nothing re-fires.
  useEffect(() => {
    if (!getToken() || !listing?.uuid || !listingIsFresh || !choicesReady || importNeedsReview)
      return;
    const stash = takeCreditsResume(listing.uuid);
    if (!stash || stash.resume.kind !== "prepare") return;
    const artifacts = Array.isArray(stash.resume.artifacts) ? stash.resume.artifacts : [];
    updateChoices({
      artifacts: artifacts.filter((key) => ADDABLE_ARTIFACTS.includes(key)),
      resumeRequested: false,
    });
    setCreditsAdded({ balance: stash.balance ?? null });
  }, [listing?.uuid, listingIsFresh, choicesReady, importNeedsReview, updateChoices]);

  // Trello #27: a Zillow import can come back with ZERO photos (Sam hit it
  // three times on one Houston condo). The listing is otherwise complete, but
  // preparing requires at least one photo for non-manual sources — and the old
  // page surfaced only a bare 409, a dead end. Offer the repair right here.
  // Trello #52: skipping photos lifts the gate — the server's flag on the
  // listing payload, or the skip that just went through on this page.
  const photoCount = listing?.images?.length || 0;
  const photosSkipped = Boolean(listing?.photos_skipped) || photosSkippedHere;
  const needsPhotos =
    !!listing && listing.source !== "manual" && photoCount === 0 && !photosSkipped;
  const preparingWithoutPhotos = !!listing && photoCount === 0 && photosSkipped;

  // Trello #38 (Sam): a Zillow page can also come back with a THIN photo set.
  // 1-2 photos is an offer to add more, never a gate — zero keeps its gate.
  const thinPhotos = !!listing && listing.source !== "manual" && photoCount > 0 && photoCount < 3;

  // Trello #38 (Sam): required facts a Zillow page can fail to carry. Only
  // the GAPS render as inputs — whatever Zillow did return stays read-only
  // and authoritative (the API enforces fill-only on its side too).
  const REQUIRED_DETAILS = [
    ["street_address", "Street address", "text"],
    ["city", "City", "text"],
    ["address_state", "State", "text"],
    ["zipcode", "ZIP", "text"],
    ["bedrooms", "Beds", "number"],
    ["bathrooms", "Baths", "number"],
    ["living_area", "Living area (sqft)", "number"],
    ["lot_size", "Lot size (sqft)", "number"],
  ];
  const missingDetails =
    listing && listing.source !== "manual"
      ? REQUIRED_DETAILS.filter(([field]) => {
          const value = /** @type {any} */ (listing)[field];
          return value === null || value === undefined || value === "";
        })
      : [];

  // Trello #52: prepare without photos. Same auth as the page's other
  // listing calls (owner, or the funnel token a guest is holding).
  const handleSkipPhotos = async () => {
    if (!listing?.uuid || skippingPhotos) return;
    setSkipPhotosError(null);
    setSkippingPhotos(true);
    try {
      await axios.post(
        `${API_URL}/listings/${listing.uuid}/skip-photos/`,
        {},
        { headers: listingAuthHeader(listing.uuid) }
      );
      setPhotosSkippedHere(true);
    } catch (error) {
      console.error(
        "Skip photos failed:",
        /** @type {any} */ (error)?.response?.data || /** @type {any} */ (error)?.message
      );
      setSkipPhotosError("Couldn't skip photos — try again.");
    } finally {
      setSkippingPhotos(false);
    }
  };

  /** @param {React.ChangeEvent<HTMLInputElement>} e */
  const handleMissingPhotosSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !listing?.uuid) return;
    if (!imageUploadLoading) void missingPhotoUpload.start(files);
    // Allow re-selecting the same files after a failure.
    e.target.value = "";
  };

  if (globalLoading) {
    return (
      <PageContainer>
        <NewNavbar />
        <ContentWrapper>
          <Container>
            <LoadingContainer>
              <Spinner size="lg" color="secondary" />
              <p>Loading your listing details…</p>
            </LoadingContainer>
          </Container>
        </ContentWrapper>
        <NewFooter />
      </PageContainer>
    );
  }

  if (bootIdRef.current && !listingIsFresh) {
    return (
      <PageContainer>
        <NewNavbar />
        <ContentWrapper>
          <Container>
            <ErrorContainer>
              {bootReadError ? (
                <>
                  <h2>We couldn’t verify this listing yet</h2>
                  <p>
                    Your saved checkout choices are kept. We’ll check the listing before starting a
                    new preparation.
                  </p>
                  <button
                    type="button"
                    onClick={() => setBootReadRevision((revision) => revision + 1)}
                  >
                    Retry listing check
                  </button>
                </>
              ) : (
                <output>Checking the latest listing details…</output>
              )}
            </ErrorContainer>
          </Container>
        </ContentWrapper>
        <NewFooter />
      </PageContainer>
    );
  }

  if (!listing) {
    return (
      <PageContainer>
        <NewNavbar />
        <ContentWrapper>
          <Container>
            <ErrorContainer>
              <h2>Listing Not Found</h2>
              <p>We couldn't find the listing you're looking for.</p>
              <button type="button" onClick={handleGoBack}>
                Go Back
              </button>
            </ErrorContainer>
          </Container>
        </ContentWrapper>
        <NewFooter />
      </PageContainer>
    );
  }

  if (importNeedsReview) {
    return (
      <PageContainer>
        <NewNavbar />
        <ContentWrapper>
          <Container>
            <ListingImportProgress listingUuid={listing.uuid} />
          </Container>
        </ContentWrapper>
        <NewFooter />
      </PageContainer>
    );
  }

  const address = formatAddress(listing);
  const facts = formatFacts(listing);
  const creditCopy = confirmationCreditCopy({
    signedIn: !!getToken(),
    account,
    loading: accountLoading,
    error: accountError,
    cost: prepareCost,
    pending: !!paidPrepare.pending || paidPrepare.busy || isPreparing,
  });
  // Get the primary/cover image - check multiple possible field names from the backend.
  // Zillow serves a Google Maps satellite tile AS the hero for photoless
  // listings — showing it here reads as "there is a photo" right next to the
  // add-photos prompt saying there isn't one (Trello #27). Treat it as no cover.
  const coverIsMapTile =
    typeof listing.cover_image_url === "string" &&
    listing.cover_image_url.includes("maps.googleapis.com");
  /** @type {string | null | undefined} */
  const primaryImage =
    (coverIsMapTile ? null : listing.cover_image_url) || // Zillow cover image URL
    (listing.images && listing.images.length > 0
      ? listing.images[0].file // First uploaded image
      : null);

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container>
          {choiceWarning && <ErrorMessage role="alert">{choiceWarning}</ErrorMessage>}
          {confirmationError && (
            <ErrorMessage role="alert">
              {flattenError(confirmationError)
                .map(({ message }) => message)
                .join(" ")}
            </ErrorMessage>
          )}
          <CheckoutCard>
            <PropertyRow>
              {primaryImage && !imageFailed ? (
                <Thumb
                  src={primaryImage}
                  alt={address}
                  // Render the placeholder via state/JSX rather than mutating
                  // innerHTML on a React-managed node (which breaks
                  // reconciliation on the next render).
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <ThumbFallback>Listing package</ThumbFallback>
              )}
              <PropertyMeta>
                <Eyebrow>Preparing your listing package for</Eyebrow>
                <PropertyAddress>{address}</PropertyAddress>
                {facts && <PropertyFacts>{facts}</PropertyFacts>}
                {/* No target-price ask here, even when the import carried no
                    asking price (card #26): suggesting the price IS the
                    service, and the manual form already collects one where the
                    agent has it. No-price listings generated fine before this
                    field existed; the pricing analysis anchors on comps. */}
                {!listingConfirmed && !confirmationUnsafeToCancel && !listing.payment_time && (
                  <NotYourProperty>
                    Not your property?{" "}
                    <button type="button" onClick={handleGoBack}>
                      Go back
                    </button>
                  </NotYourProperty>
                )}
              </PropertyMeta>
            </PropertyRow>

            <Divider />

            <DeliveryPill>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              Prepared in the background; we’ll email you when it’s ready
            </DeliveryPill>

            {(prepTemplates.length > 0 || selectedTemplate) && (
              <TemplatePickerRow>
                <FormLabel htmlFor="prepTemplate">Prep template</FormLabel>
                <FormInput
                  as="select"
                  id="prepTemplate"
                  value={selectedTemplate}
                  onChange={(e) => updateChoices({ template: e.target.value })}
                  disabled={isPreparing || !!paidPrepare.pending || paidPrepare.busy}
                >
                  <option value="">Use account default</option>
                  {selectedTemplate &&
                    !prepTemplates.some((item) => item.name === selectedTemplate) && (
                      <option value={selectedTemplate}>{selectedTemplate} (unavailable)</option>
                    )}
                  {prepTemplates.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </FormInput>
              </TemplatePickerRow>
            )}

            {missingDetails.length > 0 && (
              <MissingPhotos>
                <strong>A few details didn&rsquo;t come through from Zillow.</strong> Everything it
                did provide is already filled in — add what&rsquo;s missing so your report has the
                full picture.
                <MissingDetailsGrid>
                  {missingDetails.map(([field, label, type]) => (
                    <div key={field}>
                      <label htmlFor={`missing-${field}`}>{label}</label>
                      <input
                        id={`missing-${field}`}
                        type={type}
                        inputMode={type === "number" ? "decimal" : undefined}
                        min={type === "number" ? 0 : undefined}
                        value={detailFixes[field] || ""}
                        disabled={isPreparing || !!paidPrepare.pending || paidPrepare.busy}
                        onChange={(e) => {
                          // React 16 pools synthetic events: the functional
                          // updater runs after the event is nullified, so the
                          // value must be read while the event is live.
                          const value = e.target.value;
                          updateChoices({
                            detailFixes: { ...choicesRef.current?.detailFixes, [field]: value },
                          });
                        }}
                      />
                    </div>
                  ))}
                </MissingDetailsGrid>
              </MissingPhotos>
            )}

            <PhotoImportCoverage importState={listing?.import} />

            {preparingWithoutPhotos && (
              <PhotosSkippedNote data-testid="photos-skipped">
                Preparing without photos. You can add photos later in Personalize; the photo review
                and touch-ups need them.
              </PhotosSkippedNote>
            )}

            {(needsPhotos || thinPhotos) && (
              <MissingPhotos>
                {needsPhotos ? (
                  <>
                    <strong>This import didn&rsquo;t include photos.</strong> Zillow pages for some
                    units don&rsquo;t expose them. Add photos now, or skip them and add photos later
                    in Personalize.
                  </>
                ) : (
                  <>
                    <strong>
                      This listing has {photoCount} photo{photoCount === 1 ? "" : "s"}.
                    </strong>{" "}
                    You can continue as-is, but a few more photos make for a stronger photo
                    assessment and a better-looking package.
                  </>
                )}
                <MissingPhotosActions>
                  <label htmlFor="missing-photos-input">
                    {imageUploadLoading ? "Uploading…" : "Add photos"}
                  </label>
                  <input
                    id="missing-photos-input"
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={
                      imageUploadLoading || missingPhotoUpload.busy || intakeCooldown.blocked
                    }
                    onChange={handleMissingPhotosSelected}
                  />
                  {needsPhotos && (
                    <button
                      type="button"
                      onClick={handleSkipPhotos}
                      disabled={skippingPhotos || imageUploadLoading}
                    >
                      {skippingPhotos ? "Skipping…" : "Skip photos"}
                    </button>
                  )}
                </MissingPhotosActions>
                {skipPhotosError && <ErrorMessage role="alert">{skipPhotosError}</ErrorMessage>}
                {(imageUploadError || intakeCooldown.blocked) && (
                  <ErrorMessage role="alert">
                    {intakeErrorText(imageUploadError, intakeCooldown.retryAt) ||
                      flattenError(imageUploadError)
                        .map(({ message }) => message)
                        .join(" ")}
                  </ErrorMessage>
                )}
                {missingPhotoUpload.files.length > 0 && (
                  <div>
                    <p>
                      Your selected photos are kept on this page. Leaving or refreshing requires
                      selecting them again.
                    </p>
                    <button
                      type="button"
                      disabled={
                        imageUploadLoading || missingPhotoUpload.busy || intakeCooldown.blocked
                      }
                      onClick={() => void missingPhotoUpload.retry()}
                    >
                      Retry selected photos
                    </button>
                    <button
                      type="button"
                      disabled={imageUploadLoading || missingPhotoUpload.busy}
                      onClick={missingPhotoUpload.discard}
                    >
                      Discard selection
                    </button>
                  </div>
                )}
              </MissingPhotos>
            )}

            <div data-testid="prepare-sheet">
              {!primerSeen && (
                <Primer data-testid="credits-primer">
                  <p>Credits pay for what Listino prepares.</p>
                  <p>Editing and sharing your web report don’t use additional credits.</p>
                  <p>If anything fails, credits come back automatically.</p>
                </Primer>
              )}
              <MenuLine>
                {ARTIFACT_LABELS.analysis}
                <span>· {formatCredits(ACTION_COSTS.analysis)}</span>
              </MenuLine>
              <MenuSub>{ANALYSIS_CONTENTS}</MenuSub>
              <MenuSub>
                The analysis is the core report. The extras below are optional and cost additional
                credits; you don’t need to select any to prepare and share your web report.
              </MenuSub>

              <AddFilesLabel>Add files now (optional)</AddFilesLabel>
              {ADDABLE_ARTIFACTS.map((key) => {
                const cost = ACTION_COSTS[key];
                const note = ARTIFACT_NOTES[key];
                return (
                  <CheckRow key={key} htmlFor={`addon-${key}`}>
                    <input
                      id={`addon-${key}`}
                      type="checkbox"
                      checked={addons.includes(key)}
                      onChange={() => toggleAddon(key)}
                      disabled={isPreparing || !!paidPrepare.pending || paidPrepare.busy}
                    />
                    <span>
                      {ARTIFACT_LABELS[key]} · {formatCredits(cost)}
                      {note && key === "deck" && <em> ({note})</em>}
                    </span>
                  </CheckRow>
                );
              })}
              <MenuSub>You can add any of these later at the same price.</MenuSub>
              <MenuSub data-testid="prepare-credit-guidance" aria-live="polite">
                {creditCopy.message}
              </MenuSub>
              {(credits ? credits.plan_key === "free" || credits.watermarked : !getToken()) && (
                <MenuSub>
                  Exports on the Free plan carry a small Listino mark. Upgrading removes it from
                  everything you&rsquo;ve prepared.
                </MenuSub>
              )}

              {creditsAdded && (
                <MenuSub data-testid="credits-added">
                  {(() => {
                    const balance = creditCopy.balance;
                    return balance === null || balance === undefined
                      ? "Credits added. Your current balance is unavailable."
                      : `Credits added — you have ${formatCreditsNumber(balance)}.`;
                  })()}
                </MenuSub>
              )}

              <PrepareRow>
                <CostLine cost={prepareCost} balance={creditCopy.balance} />
                <OrderButton
                  ref={prepareButtonRef}
                  onClick={handlePrepare}
                  disabled={
                    isPreparing ||
                    paidPrepare.busy ||
                    !!paidPrepare.pending ||
                    needsPhotos ||
                    !choicesReady
                  }
                >
                  {!getToken()
                    ? "Create a free account to prepare"
                    : isPreparing
                      ? "Preparing…"
                      : "Prepare listing"}
                </OrderButton>
              </PrepareRow>
            </div>
            <FootRow>
              <Guarantee>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                If a generation fails, your credits come back automatically.
              </Guarantee>
            </FootRow>

            {paidPrepare.pending && (
              <div aria-live="polite">
                <MenuSub>
                  This purchase request is saved. Check its exact status before preparing again.
                </MenuSub>
                <OrderButton
                  type="button"
                  disabled={paidPrepare.busy}
                  onClick={() => void checkPreparation()}
                >
                  {paidPrepare.busy ? "Checking status…" : "Check preparation status"}
                </OrderButton>
              </div>
            )}
            {paidPrepare.error && <ErrorMessage role="alert">{paidPrepare.error}</ErrorMessage>}
            {checkoutError && <ErrorMessage>{checkoutError}</ErrorMessage>}
          </CheckoutCard>
        </Container>
      </ContentWrapper>
      <NewFooter />
      {paywall && (
        <CreditsPaywallSheet
          block={paywall.block}
          returnFocusRef={prepareButtonRef}
          resume={paywall.resume}
          listingUuid={listing.uuid}
          returnPath="/listing-confirmation/"
          onClose={() => setPaywall(null)}
        />
      )}
    </PageContainer>
  );
}

export default NewListingConfirmation;
